import { useState } from 'react';
import { useStore } from 'zustand';
import type { Game } from '@/game/Game';
import { useTaskCards } from '@/ui/hooks/useTaskCards';
import { useI18n } from '@/ui/i18n/I18nProvider';
import styles from './TaskCoach.module.css';

/**
 * FTUE coach (ux-screens §7): a small "Hướng dẫn 4/10" card under the HUD with the current step. Tap to read
 * Granny's hint. Never blocks the room; hidden while dragging or in a mini game.
 */
export function TaskCoach({ game, onOpen }: { game: Game; onOpen: () => void }) {
  const { t } = useI18n();
  const { cards, inTutorial, tutorialTotal } = useTaskCards();
  const [open, setOpen] = useState<string | null>(null);
  const busy = useStore(game.miniGameUi, (s) => s.run !== null);
  const dragging = useStore(game.placementUi, (s) => s.dragging !== null);
  const card = cards[0];
  if (!inTutorial || !card || busy || dragging) return null;
  const expanded = open === card.taskId;
  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={styles.card}
        aria-expanded={expanded}
        data-testid="task-coach"
        onClick={() => {
          setOpen(expanded ? null : card.taskId);
        }}
      >
        <span className={styles.step}>
          {t('ui.tasks.tutorial_step', { step: card.step ?? 1, total: tutorialTotal })}
        </span>
        <span className={styles.title}>{t(card.titleKey)}</span>
        {card.target > 1 && (
          <span className={styles.count}>
            {t('ui.tasks.progress', { current: Math.min(card.current, card.target), target: card.target })}
          </span>
        )}
      </button>
      {expanded && card.hintKey && (
        <div className={styles.hint} role="note">
          <p>{t(card.hintKey, card.hintParams)}</p>
          <button
            type="button"
            className={styles.more}
            onClick={() => {
              setOpen(null);
              onOpen();
            }}
          >
            {t('ui.tasks.see_all')}
          </button>
        </div>
      )}
    </div>
  );
}
